import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLandingContent } from '@/mocks/landingData';

export default function ContactForm() {
  const { i18n } = useTranslation();
  const { contact, footer } = getLandingContent(i18n.language);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    city: '',
    industry: '',
    product: '',
    packaging: '',
    lineType: '',
    description: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'description' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    if (!formData.name || !formData.phone || !formData.email) {
      setStatus('error');
      setErrorMsg(contact.errors.required);
      return;
    }

    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => {
        if (val) params.append(key, val);
      });

      const res = await fetch(contact.formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          city: '',
          industry: '',
          product: '',
          packaging: '',
          lineType: '',
          description: '',
        });
      } else {
        setStatus('error');
        setErrorMsg(contact.errors.submit);
      }
    } catch {
      setStatus('error');
      setErrorMsg(contact.errors.connection);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-gray-50">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-start">
            <div>
              <p className="text-red-600 text-sm font-medium tracking-widest uppercase mb-4 md:mb-6">
                {contact.eyebrow}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 md:mb-8 leading-tight">
                {contact.title}
              </h2>
              <p className="text-base md:text-lg text-gray-500 leading-relaxed mb-10 md:mb-14">
                {contact.subtitle}
              </p>

              <div className="space-y-5 md:space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-red-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Email</p>
                    <p className="text-gray-900 font-medium">info@tzk-east.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-red-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{contact.labels.region}</p>
                    <p className="text-gray-900 font-medium">{footer.country}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-7 md:p-10 border border-gray-100 shadow-card">
              <form
                onSubmit={handleSubmit}
                className="space-y-5 md:space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.name} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.name}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.company}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.company}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.phone} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.phone}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.email} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.email}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.city}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.city}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.industry}
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors bg-white"
                    >
                      <option value="">{contact.placeholders.industry}</option>
                      {contact.industries.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.product}
                    </label>
                    <input
                      type="text"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                      placeholder={contact.placeholders.product}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {contact.labels.packaging}
                    </label>
                    <select
                      name="packaging"
                      value={formData.packaging}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors bg-white"
                    >
                      <option value="">{contact.placeholders.packaging}</option>
                      {contact.packagingOptions.map((option) => (
                        <option key={option.value} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {contact.labels.lineType}
                  </label>
                  <select
                    name="lineType"
                    value={formData.lineType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors bg-white"
                  >
                    <option value="">{contact.placeholders.lineType}</option>
                    {contact.lineTypeOptions.map((option) => (
                      <option key={option.value} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {contact.labels.description}
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors resize-none"
                    placeholder={contact.placeholders.description}
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {formData.description.length}/500
                  </p>
                </div>

                {status === 'error' && (
                  <div className="p-4 rounded-md bg-red-50 text-red-600 text-sm">
                    {errorMsg}
                  </div>
                )}
                {status === 'success' && (
                  <div className="p-4 rounded-md bg-green-50 text-green-700 text-sm flex items-center gap-2">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center"></i>
                    {contact.success}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-hover disabled:opacity-60 text-white font-medium px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>
                      {contact.submitting}
                    </>
                  ) : (
                    <>
                      {contact.submitText}
                      <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  {contact.consent}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
